import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PasslyCheckbox } from '@app/components';
import { handleAllGroupsForSelect, handleRolesForSelect } from '@app/store/actions';
import { ICustomSelect, Order, TranslationsTypes } from '@app/models';
import { resolveOrgName } from '@app/helpers';
import { PasslyAutoComplete } from '@app/componentsV2';

interface IProps {
    type: string;
    label?: string;
    checkValidation: Function;
    patchData: string[] | string;
}

enum ChipType {
    roles = 'Roles',
    groups = 'Groups',
    notInGroups = 'NotInGroups'
}

export const ChipSelector = (props: IProps) => {
    const { type, label, checkValidation, patchData } = props;
    const dispatch = useDispatch();

    const orgs = useSelector((state: any) => state.basic.shellConfig.organizations);
    const [list, setList] = useState<ICustomSelect[]>([]);
    const [chips, setChips] = useState<any[]>([]);
    const [patchChips, setPatchChips] = useState<string[]>(typeof patchData !== 'string' ? patchData : []);
    const [chipType, setChipType] = useState<ChipType>(type as ChipType);

    const groups = useSelector((state: any) => state.groups.groupsForSelect);
    const roles = useSelector((state: any) => state.roles.rolesForSelect);
    const organizationId = useSelector((state: any) => state.organizationSelect.selectedOrganization);

    const handleChange = (e: any) => {
        const chosen = e.target.value;

        chosen && setChips(chosen);
    };

    const resetComponent = () => {
        setList([]);

        if (patchChips?.length) {
            const chipsToset = patchChips.map(patchChip => {
                return {name: patchChip};
            });

            setChips(chipsToset);
            setPatchChips([]);
        } else {
            setChips([]);
        }
    };

    const handleSelectByType = (type: ChipType): void => {
        switch (type) {
            case ChipType.groups:
            case ChipType.notInGroups:
                setChipType(ChipType.groups);
                dispatch(handleAllGroupsForSelect(100));
                break;
            case ChipType.roles:
                setChipType(ChipType.roles);
                dispatch(handleRolesForSelect('Name', Order.asc, 100, 0, organizationId, []));
                break;
            default:
                // TODO: Uncomment when we will set up dev variable
                // console.error(translation('APP_ERROR'));
                break;
        }
    };

    const checkItem = (chosen: any, isChecked: boolean) => (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        const chipIndex = chips.findIndex(chip => chip.name === chosen.name );

        if (!isChecked && chipIndex === -1) {
            setChips([...chips, chosen]);
        } else {
            const chipsResult = [...chips];

            chipsResult.splice(chipIndex, 1);
            setChips(chipsResult);
        }

    };

    const getSelectorItem = (item: any) => {
        if (!item) return <></>;
        const isChecked = !!chips.find((chip: any) => chip.name === item.name);

        return (
            <div className='chipSelector__item' onClick={checkItem(item, isChecked)}>
                <PasslyCheckbox className='chipSelector__checkbox' name='active' checked={isChecked} label='' />
                <div className='chipSelector__text'>{item.name}</div>
            </div>
        );
    };

    useEffect(() => {
        resetComponent();
        handleSelectByType(type as ChipType);
    }, [type]);

    useEffect(() => {
        if (type === ChipType.groups || type === ChipType.notInGroups) {
            const groupsWithOrgs = groups.map((group: ICustomSelect) => {
                return {
                    id: group.id,
                    name: `${group.name} (${group.organizationId ? resolveOrgName(group.organizationId, orgs) : ''})`,
                };
            });
            setList(groupsWithOrgs);
        }
    }, [groups]);

    useEffect(() => {
        if (type === ChipType.roles) {
            setList(roles);
        }
    }, [roles]);

    useEffect(() => {
        if (chips.length) {
            const chipsToSend = chips.map(chip => chip.name || chip);

            checkValidation(true, chipsToSend);
        } else {
            checkValidation(false, null);
        }
    }, [chips, type]);

    return (
        <div className='chipSelector'>
            <div className='chipSelector__select'>
                <PasslyAutoComplete
                    label={label || type as TranslationsTypes}
                    id={chipType === ChipType.roles ? 'chipSelect' : 'groupSelection'}
                    multiple
                    value={chips}
                    onChange={handleChange}
                    itemTemplate={getSelectorItem}
                    dropdown={true}
                    items={list}
                    field='name'
                />
            </div>
        </div>
    );
};
